import { PurlivoAccountSwitch } from '@shared/common/session/purlivo-account-switch';
import { PurlivoLocale } from '@shared/common/purlivo-locale';
import { PurlivoBrowserContext } from '@shared/common/session/purlivo-browser-context';
import { Injectable, inject } from '@angular/core';
import { AbpHttpInterceptor, MessageService } from 'abp-ng2-module';
import { HttpRequest, HttpHandler, HttpEvent, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { Observable, switchMap, throwError, takeUntil, from, of } from 'rxjs';
import { HttpErrorParserService } from '@shared/common/http-error-parser.service';

@Injectable({
    providedIn: 'root',
})
export class ZeroTemplateHttpInterceptor extends AbpHttpInterceptor {
    private messageService: MessageService = new MessageService();
    private httpErrorParser = inject(HttpErrorParserService);

    override intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!PurlivoBrowserContext.check()) return throwError(() => new Error('PurlivoContextChanged'));
        return super.intercept(request.clone({ setHeaders: { 'Accept-Language': PurlivoLocale.requestLanguage() } }), next).pipe(takeUntil(PurlivoBrowserContext.invalidated));
    }

    protected override tryGetRefreshTokenService(): Observable<boolean> {
        return from(this.preserveNativeHandoff()).pipe(switchMap(preserve =>
            preserve ? of(false) : super.tryGetRefreshTokenService()));
    }

    protected override handleErrorResponse(error: any): Observable<never> {
        if (error instanceof HttpErrorResponse && error.status === 401) {
            return from(this.preserveNativeHandoff()).pipe(switchMap(preserve =>
                preserve ? throwError(() => error) : this.handleNativeErrorResponse(error)));
        }
        return this.handleNativeErrorResponse(error);
    }

    private async preserveNativeHandoff(): Promise<boolean> {
        // SwitchToLinkedAccount revokes the old session before issuing the handoff token.
        // A late 401 must still fail, but cannot refresh/replay old work or replace that handoff.
        if (!PurlivoBrowserContext.check()) return true;
        const switching = await PurlivoAccountSwitch.isInProgress();
        return switching || !PurlivoBrowserContext.check();
    }

    private handleNativeErrorResponse(error: any): Observable<never> {
        this.addParsedMessageToError(error);

        if (!(error.error instanceof Blob)) {
            this.messageService.error('Server is unreachable');
            return throwError(() => error);
        }

        return this.configuration.blobToText(error.error).pipe(
            switchMap((json) => {
                const errorBody = json === '' || json === null ? {} : JSON.parse(json);
                const errorResponse = new HttpResponse({
                    headers: error.headers,
                    status: error.status,
                    body: errorBody,
                });

                let ajaxResponse = this.configuration.getAbpAjaxResponseOrNull(errorResponse);

                if (ajaxResponse != null) {
                    this.configuration.handleAbpResponse(errorResponse, ajaxResponse);
                } else {
                    this.configuration.handleNonAbpErrorResponse(errorResponse);
                }

                return throwError(() => error);
            })
        );
    }

    private addParsedMessageToError(error: any): void {
        if (!(error instanceof HttpErrorResponse)) {
            return;
        }

        const parsedError = this.httpErrorParser.parseError(error, 'An error occurred');

        if (typeof error.error === 'object' && error.error !== null && !(error.error instanceof Blob)) {
            if (!error.error.message) {
                error.error.message = parsedError.message;
            }
        } else if (typeof error.error === 'string') {
            const originalError = error.error;
            (error as any).error = {
                originalError: originalError,
                message: parsedError.message,
            };
        } else if (!error.error || error.error === null) {
            (error as any).error = {
                message: parsedError.message,
            };
        }
    }
}
